import { APIError, OrganisationDTO, UserListItemDTO, UserType } from "@bvhi/backend-api";
import { Box, GridColDef, Typography } from "@bvhi/ui-base";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ActionCells } from "../../../../../../components/datagrid/cells/action-cells";
import { DateCell } from "../../../../../../components/datagrid/cells/date-cell";
import { UserDetailLinkCell } from "../components/datagrid/user-detail-link-cell";
import { UserStatusCell } from "../components/datagrid/user-status-cell";
import { UserTypeCell } from "../components/datagrid/user-type-cell";
import { useUsersSortParams } from "./use-users-sort-params";
import { AudiologistsPrimaryChannelsCell } from "../../../audiologists/list/components/datagrid/audiologists-primary-channels-cell";
import { useAuthContext } from "../../../../../../context/use-auth-context";
import { hasFeatureEnabled } from "../../../../../../utils/env-helper";

export const useUsersDataGridDefinitions = (handleDelete: (user: UserListItemDTO) => void) => {
  const { currentUser } = useAuthContext();
  if (!currentUser) {
    throw new APIError("CurrentUser does not exist.", {
      status: 401,
      code: "UNAUTHORIZED",
      message: "CurrentUser does not exist.",
      timestamp: new Date().toISOString(),
    });
  }

  const { t } = useTranslation(["users"]);
  const { isFieldSortable } = useUsersSortParams();
  const orderApiEnabled = useMemo(() => hasFeatureEnabled("ENABLE_ORDER_API"), []);

  const columns: GridColDef<UserListItemDTO>[] = useMemo(() => {
    return [
      {
        field: "lastName",
        headerName: t("users:datagrid.lastName"),
        minWidth: 150,
        width: 200,
        maxWidth: 250,
        sortable: isFieldSortable("lastName"),
        renderCell: (params) => (
          <Box alignItems="center" display="flex" sx={{ width: "100%", height: "100%" }}>
            <UserDetailLinkCell label={`${params.row.lastName}, ${params.row.firstName}`} userId={params.row.id ? params.row.id : "-1"} />
          </Box>
        ),
      },
      {
        field: "username",
        headerName: t("users:datagrid.username"),
        minWidth: 200,
        width: 275,
        maxWidth: 350,
        sortable: isFieldSortable("username"),
        renderCell: (params) => (
          <Box alignItems="center" display="flex" sx={{ width: "100%", height: "100%" }}>
            <Typography>{params.value}</Typography>
          </Box>
        ),
      },
      {
        field: "email",
        headerName: t("users:datagrid.email"),
        minWidth: 200,
        width: 275,
        maxWidth: 350,
        sortable: isFieldSortable("email"),
        renderCell: (params) => (
          <Box alignItems="center" display="flex" sx={{ width: "100%", height: "100%" }}>
            <Typography>{params.value}</Typography>
          </Box>
        ),
      },
      {
        field: "type",
        headerName: t("users:datagrid.type"),
        minWidth: 100,
        width: 150,
        maxWidth: 200,
        sortable: isFieldSortable("type"),
        renderCell: (params) => (
          <Box alignItems="center" display="flex" sx={{ width: "100%", height: "100%" }}>
            <UserTypeCell data={params.row.type} />
          </Box>
        ),
      },
      {
        field: "organisation",
        headerName: t("users:datagrid.organisation"),
        minWidth: 150,
        width: 200,
        maxWidth: 250,
        sortable: isFieldSortable("type"),
        valueGetter: (value?: OrganisationDTO) => (value?.name ? value.name : ""),
        renderCell: (params) => (
          <Box alignItems="center" display="flex" sx={{ width: "100%", height: "100%" }}>
            <Typography>{params.value}</Typography>
          </Box>
        ),
      },
      {
        field: "status",
        headerName: t("users:datagrid.status"),
        minWidth: 100,
        width: 200,
        maxWidth: 300,
        sortable: isFieldSortable("status"),
        renderCell: (params) => (
          <Box alignItems="center" display="flex" sx={{ width: "100%", height: "100%" }}>
            <UserStatusCell data={params.row.status} hasLoggedIn={Boolean(params.row.lastLoginAt)} />
          </Box>
        ),
      },
      ...(currentUser.type === UserType.ADMIN && orderApiEnabled
        ? ([
            {
              field: "channels",
              headerName: t("users:datagrid.channels"),
              minWidth: 100,
              width: 300,
              maxWidth: 500,
              sortable: isFieldSortable("channels"),
              renderCell: (params) => {
                if (params.row.type !== UserType.AUDIOLOGIST) {
                  return <Box sx={{ width: "100%", height: "100%" }} />;
                }
                return (
                  <Box alignItems="center" display="flex" py={1} sx={{ width: "100%", height: "100%" }}>
                    <AudiologistsPrimaryChannelsCell channels={params.row.channels} />
                  </Box>
                );
              },
            },
          ] as GridColDef<UserListItemDTO>[])
        : []),
      {
        field: "supportId",
        headerName: t("users:datagrid.supportId"),
        minWidth: 150,
        width: 200,
        maxWidth: 250,
        sortable: isFieldSortable("supportId"),
        renderCell: (params) => (
          <Box alignItems="center" display="flex" sx={{ width: "100%", height: "100%" }}>
            <Typography>{params.value}</Typography>
          </Box>
        ),
      },
      {
        field: "createdAt",
        headerName: t("users:datagrid.createdAt"),
        minWidth: 150,
        width: 200,
        maxWidth: 250,
        sortable: isFieldSortable("createdAt"),
        renderCell: (params) => (
          <Box alignItems="center" display="flex" sx={{ width: "100%", height: "100%" }}>
            <DateCell date={params.row.createdAt} />
          </Box>
        ),
      },
      {
        field: "updatedAt",
        headerName: t("users:datagrid.updatedAt"),
        minWidth: 150,
        width: 200,
        maxWidth: 250,
        sortable: isFieldSortable("updatedAt"),
        renderCell: (params) => (
          <Box alignItems="center" display="flex" sx={{ width: "100%", height: "100%" }}>
            <DateCell date={params.row.updatedAt} />
          </Box>
        ),
      },
      {
        field: "id",
        headerName: t("users:datagrid.actions"),
        flex: 1,
        minWidth: 125,
        headerClassName: "datagrid-sticky-right",
        cellClassName: "datagrid-sticky-right",
        align: "right",
        headerAlign: "right",
        sortable: isFieldSortable("id"),
        resizable: false,
        renderCell: (params) => (
          <ActionCells
            deletePermission="user:delete"
            detailParams={{ userId: params.row.id }}
            detailPermission="user:read"
            detailRoute="/app/users/$userId"
            handleDelete={() => handleDelete(params.row)}
            id={params.row.id}
            showDeleteAction={true}
            showDetailAction={true}
          />
        ),
      },
    ];
  }, [t, isFieldSortable, currentUser, orderApiEnabled, handleDelete]);

  return { columns };
};
